'use client'

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Header } from "@/components/header"
import { Check, ArrowRight, Zap, TrendingUp, DollarSign, Brain, Wrench, Target, BarChart3, Gift, X, TrendingDown, Package, History, Eye, Sparkles } from "lucide-react"
import Link from "next/link"
import Image from "next/image"
import { useEffect, useState, useRef, useMemo, useCallback } from "react"
import { useRouter } from "next/navigation"
import { useAuth } from "@/contexts/auth-context"
import { LOGO_SRC } from "@/lib/brand"
import { toast } from "sonner"

export default function HomePage() {
  const router = useRouter()
  const { isAuthenticated, isLoading, startGuestDemo, user } = useAuth()
  const isGuestUser = user?.role === 'guest'
  const [visibleSections, setVisibleSections] = useState<Set<string>>(new Set())
  const sectionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({})
  const metricsWheelRef = useRef<HTMLDivElement>(null)
  const [scrollPosition, setScrollPosition] = useState(0)
  const targetScrollRef = useRef(0)
  const currentScrollRef = useRef(0)
  const [isScreenshotOpen, setIsScreenshotOpen] = useState(false)
  const [isScreenshotAnimating, setIsScreenshotAnimating] = useState(false)
  const [isStartingGuestDemo, setIsStartingGuestDemo] = useState(false)

  // Редирект для залогиненных пользователей
  useEffect(() => {
    if (!isLoading && isAuthenticated && !isStartingGuestDemo && !isGuestUser) {
      router.push('/rnp')
    }
  }, [isLoading, isAuthenticated, isStartingGuestDemo, isGuestUser, router])

  useEffect(() => {
    // Пока авторизация не определилась или будет редирект — observer не нужен.
    // Иначе он инициализируется на "пустом" рендере (return null) и не подписывается на секции.
    if (isLoading || (isAuthenticated && !isGuestUser)) return

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev: Set<string>) => new Set(prev).add(entry.target.id))
          } else {
            setVisibleSections((prev: Set<string>) => {
              const newSet = new Set(prev)
              newSet.delete(entry.target.id)
              return newSet
            })
          }
        })
      },
      { threshold: 0.1, rootMargin: '0px 0px -100px 0px' }
    )

    Object.values(sectionRefs.current).forEach((ref) => {
      if (ref) observer.observe(ref)
    })

    return () => {
      observer.disconnect()
    }
  }, [isLoading, isAuthenticated, isGuestUser])

  const handleStartGuestDemo = useCallback(async () => {
    try {
      setIsStartingGuestDemo(true)
      const demoShopId = await startGuestDemo()
      router.push(`/rnp/${demoShopId}`)
    } catch (error) {
      console.error('Не удалось открыть демо:', error)
      toast.error('Не удалось открыть демо. Попробуйте снова.', {
        duration: Infinity,
      })
    } finally {
      setIsStartingGuestDemo(false)
    }
  }, [router, startGuestDemo])

  const isVisible = (id: string) => visibleSections.has(id)

  // Все метрики для барабана
  const allMetrics = [
    { group: 'По кабинету', name: 'Количество заказов' },
    { group: 'По кабинету', name: 'Расчетное количество выкупов' },
    { group: 'По кабинету', name: 'Расчетная валовая прибыль' },
    { group: 'По кабинету', name: 'Валовая маржа на продажу' },
    { group: 'По кабинету', name: 'Средняя валовая маржа на продажу' },
    { group: 'По кабинету', name: 'Суммарные затраты на РК' },
    { group: 'По кабинету', name: 'Затраты WB MEDIA' },
    { group: 'По кабинету', name: 'Выручка заказы' },
    { group: 'По кабинету', name: 'Расчетная выручка продажи' },
    { group: 'По кабинету', name: 'ДРР расч. продажи' },
    { group: 'По кабинету', name: 'ДРР факт. продажи' },
    { group: 'По кабинету', name: 'ДРР заказы' },
    { group: 'По кабинету', name: 'Фактические выкупы, шт.' },
    { group: 'По кабинету', name: 'Фактические выкупы, руб.' },
    { group: 'По кабинету', name: 'Общее количество остатков' },
    { group: 'Основные параметры', name: 'Заказы' },
    { group: 'Основные параметры', name: 'Заказы, руб.' },
    { group: 'Основные параметры', name: 'Стоимость с СПП' },
    { group: 'Основные параметры', name: 'Расчетный процент выкупа' },
    { group: 'Основные параметры', name: 'Расчетное число выкупов' },
    { group: 'Основные параметры', name: 'Расчетная выручка продажи' },
    { group: 'Основные параметры', name: 'Рекламный бюджет' },
    { group: 'Основные параметры', name: 'ДРР (TACOS) по расч. продажам' },
    { group: 'Основные параметры', name: 'ДРР (TACOS) по факт. продажам' },
    { group: 'Основные параметры', name: 'ДРР (TACOS) по заказам' },
    { group: 'Основные параметры', name: 'Фактические выкупы, шт.' },
    { group: 'Основные параметры', name: 'Фактические выкупы, руб.' },
    { group: 'Основные параметры', name: 'Валовая маржа в день' },
    { group: 'Основные параметры', name: 'Сумма на складах' },
    { group: 'Юнитка', name: 'Стоимость без СПП' },
    { group: 'Юнитка', name: 'СПП' },
    { group: 'Юнитка', name: 'Маркетинговый расход на 1 продажу' },
    { group: 'Юнитка', name: 'Маркетинговый расход на 1 заказ' },
    { group: 'Юнитка', name: 'Логистика с учетом % выкупа' },
    { group: 'Юнитка', name: 'Хранение' },
    { group: 'Юнитка', name: 'Валовая маржа на продажу' },
    { group: 'Юнитка', name: 'Маржинальность' },
    { group: 'Остатки', name: 'Остатки (сумма по всем размерам)' },
    { group: 'Остатки', name: 'Остатки' },
    { group: 'Остатки', name: 'Остатки суммарно учитывая % выкупа' },
    { group: 'Остатки', name: 'Рассчетная оборачиваемость' },
    { group: 'Конверсии', name: 'Показы за день' },
    { group: 'Конверсии', name: 'Показы за день органика' },
    { group: 'Конверсии', name: 'Показы за день реклама' },
    { group: 'Конверсии', name: 'CTR карточки за день' },
    { group: 'Конверсии', name: 'Клики за день' },
    { group: 'Конверсии', name: 'Клики за день, органика' },
    { group: 'Конверсии', name: 'Клики за день, реклама' },
    { group: 'Конверсии', name: 'Доля рекламного трафика' },
    { group: 'Конверсии', name: 'Корзина за день' },
    { group: 'Конверсии', name: 'Корзина за день, органика' },
    { group: 'Конверсии', name: 'Корзина за день, реклама' },
    { group: 'Конверсии', name: 'Конверсия в корзину' },
    { group: 'Конверсии', name: 'Заказы за день, органика' },
    { group: 'Конверсии', name: 'Заказы за день, реклама' },
    { group: 'Конверсии', name: 'Конверсия в заказ' },
    { group: 'Конверсии', name: 'Из клика в заказ' },
    { group: 'Конверсии', name: 'Из просмотра в заказ' },
    { group: 'Ручная ставка', name: 'Ставка' },
    { group: 'Ручная ставка', name: 'Расход' },
    { group: 'Ручная ставка', name: 'Показы' },
    { group: 'Ручная ставка', name: 'Клики' },
    { group: 'Ручная ставка', name: 'Заказы' },
    { group: 'Ручная ставка', name: 'CPC' },
    { group: 'Ручная ставка', name: 'CTR' },
    { group: 'Ручная ставка', name: 'CPO' },
    { group: 'Ручная ставка', name: 'CPM' },
    { group: 'Ручная ставка', name: 'Маркетинговый расход на продажу' },
    { group: 'Ручная ставка', name: 'ACOS по продажам' },
    { group: 'Ручная ставка', name: 'ACOS по заказам' },
    { group: 'Единая ставка', name: 'Ставка' },
    { group: 'Единая ставка', name: 'Расход' },
    { group: 'Единая ставка', name: 'Показы' },
    { group: 'Единая ставка', name: 'Клики' },
    { group: 'Единая ставка', name: 'Заказы' },
    { group: 'Единая ставка', name: 'CPC' },
    { group: 'Единая ставка', name: 'CTR' },
    { group: 'Единая ставка', name: 'CPO' },
    { group: 'Единая ставка', name: 'CPM' },
    { group: 'Единая ставка', name: 'Средняя позиция' },
    { group: 'Единая ставка', name: 'Маркетинговый расход на продажу' },
    { group: 'Единая ставка', name: 'ACOS по продажам' },
    { group: 'Единая ставка', name: 'ACOS по заказам' },
    { group: 'Ставка CPC', name: 'Ставка' },
    { group: 'Ставка CPC', name: 'Расход' },
    { group: 'Ставка CPC', name: 'Показы' },
    { group: 'Ставка CPC', name: 'Клики' },
    { group: 'Ставка CPC', name: 'Заказы' },
    { group: 'Ставка CPC', name: 'CPC' },
    { group: 'Ставка CPC', name: 'CTR' },
    { group: 'Ставка CPC', name: 'CPO' },
    { group: 'Ставка CPC', name: 'CPM' },
    { group: 'Ставка CPC', name: 'Средняя позиция' },
    { group: 'Ставка CPC', name: 'Маркетинговый расход на продажу' },
    { group: 'Ставка CPC', name: 'ACOS по продажам' },
    { group: 'Ставка CPC', name: 'ACOS по заказам' },
  ]

  // Определяем iOS для оптимизаций
  const isIOS = useMemo(() => {
    if (typeof window === 'undefined') return false
    return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
           (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  }, [])

  // Виртуализация: вычисляем видимые элементы
  const visibleMetrics = useMemo(() => {
    const centerY = scrollPosition + 150
    const visibleRange = 400 // Видимая область + буфер
    const itemHeight = 60
    const startIndex = Math.max(0, Math.floor((centerY - visibleRange / 2) / itemHeight))
    const endIndex = Math.min(allMetrics.length - 1, Math.ceil((centerY + visibleRange / 2) / itemHeight))
    
    return allMetrics.slice(startIndex, endIndex + 1).map((metric, relativeIndex) => {
      const index = startIndex + relativeIndex
      const yPos = index * itemHeight
      const distance = Math.abs(yPos - centerY)
      const scale = Math.max(0.6, 1 - distance / 250)
      const opacity = Math.max(0.2, 1 - distance / 350)
      // Упрощаем 3D трансформации для iOS
      const rotateX = isIOS ? 0 : (yPos - centerY) / 12
      const translateZ = isIOS ? 0 : Math.max(-150, (150 - distance) * 0.6)
      const zIndex = Math.round(100 - distance / 5)
      
      return {
        index,
        metric,
        yPos,
        scale,
        opacity,
        rotateX,
        translateZ,
        zIndex
      }
    })
  }, [scrollPosition, isIOS])

  useEffect(() => {
    // После isLoading ref барабана появляется в DOM — иначе слушатели не навешиваются.
    if (isLoading || (isAuthenticated && !isGuestUser)) return

    const wheel = metricsWheelRef.current
    if (!wheel) return

    let touchStartY = 0
    let mouseStartY = 0
    let isDragging = false
    let isMouseDragging = false
    let animationFrameId: number | null = null
    let lastUpdateTime = 0
    const throttleDelay = isIOS ? 16 : 8 // Throttle для iOS

    // Инициализация
    targetScrollRef.current = scrollPosition
    currentScrollRef.current = scrollPosition

    // Плавное обновление позиции через requestAnimationFrame с throttling
    const smoothUpdate = (timestamp: number) => {
      const diff = targetScrollRef.current - currentScrollRef.current
      if (Math.abs(diff) > 0.1) {
        const now = timestamp || performance.now()
        if (now - lastUpdateTime >= throttleDelay) {
          currentScrollRef.current += diff * 0.15 // Более быстрое обновление для плавности
          currentScrollRef.current = Math.max(0, Math.min(currentScrollRef.current, allMetrics.length * 60 - 300))
          setScrollPosition(currentScrollRef.current)
          lastUpdateTime = now
        }
        animationFrameId = requestAnimationFrame(smoothUpdate)
      } else {
        currentScrollRef.current = targetScrollRef.current
        setScrollPosition(targetScrollRef.current)
        animationFrameId = null
      }
    }

    const maxScroll = allMetrics.length * 60 - 300

    const handleWheel = (e: WheelEvent) => {
      const atTop = targetScrollRef.current <= 0
      const atBottom = targetScrollRef.current >= maxScroll - 1
      const scrollingDown = e.deltaY > 0
      const scrollingUp = e.deltaY < 0

      // На границах барабана — прокрутка уходит на страницу
      if ((scrollingDown && atBottom) || (scrollingUp && atTop)) {
        return
      }

      e.preventDefault()
      targetScrollRef.current = Math.max(0, Math.min(
        targetScrollRef.current + e.deltaY * 0.2,
        maxScroll
      ))
      if (!animationFrameId) {
        animationFrameId = requestAnimationFrame(smoothUpdate)
      }
    }

    const handleMouseDown = (e: MouseEvent) => {
      e.preventDefault()
      mouseStartY = e.clientY
      isMouseDragging = true
      wheel.style.cursor = 'grabbing'
    }

    const handleMouseMove = (e: MouseEvent) => {
      if (!isMouseDragging) return
      e.preventDefault()
      const deltaY = mouseStartY - e.clientY
      mouseStartY = e.clientY
      targetScrollRef.current = Math.max(0, Math.min(
        targetScrollRef.current + deltaY * 0.5,
        allMetrics.length * 60 - 300
      ))
      if (!animationFrameId) {
        animationFrameId = requestAnimationFrame(smoothUpdate)
      }
    }

    const handleMouseUp = () => {
      isMouseDragging = false
      wheel.style.cursor = 'grab'
    }

    const handleTouchStart = (e: TouchEvent) => {
      touchStartY = e.touches[0].clientY
      isDragging = true
    }

    const handleTouchMove = (e: TouchEvent) => {
      if (!isDragging) return
      const deltaY = touchStartY - e.touches[0].clientY
      const atTop = targetScrollRef.current <= 0
      const atBottom = targetScrollRef.current >= maxScroll - 1
      const scrollingDown = deltaY > 0
      const scrollingUp = deltaY < 0

      if ((scrollingDown && atBottom) || (scrollingUp && atTop)) {
        isDragging = false
        return
      }

      e.preventDefault()
      touchStartY = e.touches[0].clientY
      targetScrollRef.current = Math.max(0, Math.min(
        targetScrollRef.current + deltaY * (isIOS ? 0.6 : 0.5),
        maxScroll
      ))
      if (!animationFrameId) {
        animationFrameId = requestAnimationFrame(smoothUpdate)
      }
    }

    const handleTouchEnd = () => {
      isDragging = false
    }

    wheel.addEventListener('wheel', handleWheel, { passive: false })
    wheel.addEventListener('mousedown', handleMouseDown)
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
    wheel.addEventListener('touchstart', handleTouchStart, { passive: true })
    wheel.addEventListener('touchmove', handleTouchMove, { passive: false })
    wheel.addEventListener('touchend', handleTouchEnd)

    return () => {
      wheel.removeEventListener('wheel', handleWheel)
      wheel.removeEventListener('mousedown', handleMouseDown)
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      wheel.removeEventListener('touchstart', handleTouchStart)
      wheel.removeEventListener('touchmove', handleTouchMove)
      wheel.removeEventListener('touchend', handleTouchEnd)
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId)
      }
    }
  }, [allMetrics.length, isIOS, isLoading, isAuthenticated, isGuestUser])

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isScreenshotOpen) {
        setIsScreenshotAnimating(false)
        setTimeout(() => setIsScreenshotOpen(false), 500)
      }
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isScreenshotOpen])

  // Автоматически запускаем анимацию при открытии модального окна
  useEffect(() => {
    if (isScreenshotOpen) {
      // Небольшая задержка для правильного рендеринга
      const timer = setTimeout(() => {
        setIsScreenshotAnimating(true)
      }, 10)
      return () => clearTimeout(timer)
    } else {
      setIsScreenshotAnimating(false)
    }
  }, [isScreenshotOpen])

  // Показываем загрузку, пока проверяется авторизация
  if (isLoading) {
    return null
  }

  // Если пользователь залогинен, не показываем контент (будет редирект)
  if (isAuthenticated && !isGuestUser) {
    return null
  }


  return (
    <div className="min-h-screen relative">
      {/* Контент (фон теперь в layout.tsx) */}
      {/* Header */}
      <Header mode="home" />

      {/* Hero Section */}
      <section className="relative container mx-auto px-4 py-20 md:py-32">
        <div className="relative max-w-4xl mx-auto text-center space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
          

          <h1 className="text-4xl md:text-6xl font-bold text-balance leading-tight">
            Раньше данные были разбросаны
            <br />
            <span className="text-primary">Теперь – всё в одной таблице</span>
          </h1>

          <p className="text-xl md:text-2xl text-muted-foreground text-balance max-w-3xl mx-auto leading-[1.15]">
            Видите маржинальность и быстро реагируете, чтобы увеличить прибыль
          </p>

          <div className="flex flex-col items-center gap-4 pt-4">
            <div className="flex flex-col sm:flex-row gap-4 items-center justify-center w-full">
              <Button 
                asChild 
                size="lg" 
                className="text-base px-8 animate-in zoom-in duration-700 delay-300 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white shadow-lg shadow-purple-500/50 hover:shadow-xl hover:shadow-purple-500/60 transition-all duration-150 hover:scale-105"
              >
                <Link href="/auth/register" className="flex items-center gap-2">
                  Подключить РНП
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="text-base px-8 bg-transparent animate-in zoom-in delay-500 [animation-duration:700ms] !transition-[background-color,border-color,box-shadow] !duration-200 ease-out hover:shadow-md hover:border-primary/40"
                onClick={handleStartGuestDemo}
                disabled={isStartingGuestDemo}
              >
                {isStartingGuestDemo ? 'Открываем демо...' : 'Посмотреть демо-таблицу'}
              </Button>
            </div>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-white text-sm font-semibold" style={{ backgroundColor: '#fd9b33' }}>
              <Gift className="h-4 w-4 stroke-white" strokeWidth={2} />
              <span>3 дня бесплатно</span>
            </div>
          </div>
        </div>
      </section>

      {/* Блок про команду селлеров */}
      <section className="container mx-auto px-4 pb-10 md:pb-16 -mt-6">
        <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-r from-purple-900/60 via-purple-700/60 to-amber-500/60 shadow-2xl shadow-purple-500/30 backdrop-blur max-w-4xl mx-auto">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(255,255,255,0.12),transparent_35%),radial-gradient(circle_at_80%_10%,rgba(255,255,255,0.1),transparent_30%),radial-gradient(circle_at_60%_70%,rgba(0,0,0,0.25),transparent_40%)] animate-pulse" />
          <div className="relative px-6 py-8 md:px-8 md:py-10 space-y-3 text-center">
            <h3 className="text-2xl md:text-3xl font-bold text-white leading-tight tracking-tight drop-shadow-lg">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-white via-amber-200 to-white">
                Сделано селлерами для селлеров
              </span>
            </h3>
            <p className="text-sm md:text-base text-white/90 max-w-2xl mx-auto">
              Сервис разработан опытными селлерами, поэтому мы точно знаем, какие ключевые метрики важны для отслеживания рентабельности бизнеса и говорим с вами на одном языке!
            </p>
          </div>
        </div>
      </section>

      {/* Metrics and Screenshot Section */}
      <section className="container mx-auto px-4 py-20">
        <div className="max-w-7xl mx-auto">
          <div 
            className={`text-center mb-12 transition-all duration-700 ${
              isVisible('metrics-title') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            id="metrics-title"
            ref={(el: HTMLDivElement | null) => (sectionRefs.current['metrics-title'] = el)}
          >
            <h2 className="text-2xl md:text-3xl font-bold mb-2">
              Все ключевые метрики на одном экране
            </h2>
            <p className="text-base text-muted-foreground">
              для полного контроля над бизнесом
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 items-center">
            {/* Left: Metrics Wheel */}
            <div className="flex flex-col">
              {/* 3D Metrics Wheel */}
              <div 
                className={`transition-all duration-700 ${
                  isVisible('metrics-wheel') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                id="metrics-wheel"
                ref={(el: HTMLDivElement | null) => {
                  sectionRefs.current['metrics-wheel'] = el
                }}
              >
                <div 
                  ref={metricsWheelRef}
                  className="relative h-80 w-full overflow-hidden cursor-grab active:cursor-grabbing"
                  style={{ 
                    perspective: isIOS ? 'none' : '1000px',
                    transformStyle: 'preserve-3d',
                    WebkitTransform: 'translate3d(0,0,0)', // Аппаратное ускорение для iOS
                    willChange: 'transform'
                  }}
                >
                  <div 
                    className="relative h-full"
                    style={{
                      transform: `translate3d(0, -${scrollPosition}px, 0)`,
                      willChange: 'transform',
                      backfaceVisibility: 'hidden',
                      WebkitBackfaceVisibility: 'hidden'
                    }}
                  >
                    {visibleMetrics.map(({ index, metric, yPos, scale, opacity, rotateX, translateZ, zIndex }) => (
                      <div
                        key={index}
                        className="absolute left-0 right-0 flex items-center justify-center origin-center"
                        style={{
                          top: `${yPos}px`,
                          transform: isIOS 
                            ? `translate3d(0, 0, 0) scale(${scale})`
                            : `translate3d(0, 0, ${translateZ}px) rotateX(${rotateX}deg) scale(${scale})`,
                          opacity,
                          zIndex,
                          willChange: 'transform, opacity',
                          backfaceVisibility: 'hidden',
                          WebkitBackfaceVisibility: 'hidden'
                        }}
                      >
                        <div className={`bg-card/90 border border-border/50 rounded-lg px-4 py-2 shadow-lg w-full max-w-xs text-center ${isIOS ? '' : 'backdrop-blur-sm'}`}>
                          <div className="text-xs text-muted-foreground mb-1 font-medium">{metric.group}</div>
                          <div className="text-sm font-semibold text-foreground">{metric.name}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                </div>
                
                <div className="text-center mt-4 text-xs text-muted-foreground">
                  Прокрутите колесо мыши или потяните для просмотра
                </div>
              </div>
            </div>

            {/* Right: Screenshot */}
            <div 
              className={`transition-all duration-700 ${
                isVisible('screenshot') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              id="screenshot"
              ref={(el: HTMLDivElement | null) => (sectionRefs.current['screenshot'] = el)}
            >
              <div 
                className="relative group cursor-pointer"
                onClick={() => {
                  setIsScreenshotOpen(true)
                  // Запускаем анимацию после небольшой задержки для правильного рендеринга
                  setTimeout(() => setIsScreenshotAnimating(true), 10)
                }}
              >
                <div className="absolute -inset-1 bg-gradient-to-r from-purple-600 to-purple-400 rounded-2xl blur-xl opacity-30 group-hover:opacity-50 transition duration-1000 group-hover:duration-200"></div>
                <div className="relative bg-card/50 backdrop-blur-sm border border-border rounded-2xl p-2 shadow-2xl transition-transform duration-300 group-hover:scale-[1.02]">
                  <Image 
                    src="/main.png" 
                    alt="РНП интерфейс - все метрики на одном экране" 
                    width={1200}
                    height={800}
                    className="w-full h-auto rounded-lg"
                    priority
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits Section */}
      <section id="features" className="container mx-auto px-4 py-20">
        <div className="max-w-6xl mx-auto">
          <div 
            className={`text-center mb-12 transition-all duration-700 ${
              isVisible('features-title') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            id="features-title"
            ref={(el: HTMLDivElement | null) => (sectionRefs.current['features-title'] = el)}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ключевые преимущества
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            {/* Benefit 1 */}
            <Card 
              className={`bg-card/50 backdrop-blur-sm border-border transition-all duration-700 hover:border-primary/50 hover:shadow-lg ${
                isVisible('benefit-1') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              id="benefit-1"
              ref={(el: HTMLDivElement | null) => (sectionRefs.current['benefit-1'] = el)}
            >
              <CardHeader>
                <div className="flex flex-col items-center text-center gap-3">
                  <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                    <DollarSign className="h-6 w-6 text-primary" />
                  </div>
                  <CardTitle className="text-lg">Вся прибыль в одном окне</CardTitle>
                  <CardDescription className="text-sm">
                    Итоговая маржинальность по каждому артикулу. Чистый результат после всех комиссий, логистики и рекламы
                  </CardDescription>
                </div>
              </CardHeader>
            </Card>

            {/* Benefit 2 */}
            <Card 
              className={`bg-card/50 backdrop-blur-sm border-border transition-all duration-700 delay-100 hover:border-primary/50 hover:shadow-lg ${
                isVisible('benefit-2') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              id="benefit-2"
              ref={(el: HTMLDivElement | null) => (sectionRefs.current['benefit-2'] = el)}
            >
              <CardHeader>
                <div className="flex flex-col items-center text-center gap-3">
                  <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Zap className="h-6 w-6 text-primary" />
                  </div>
                  <CardTitle className="text-lg">Быстрая реакция</CardTitle>
                  <CardDescription className="text-sm">
                    За 2 клика находите причину падения маржи. Решаете проблемы быстрее - не теряете прибыль
                  </CardDescription>
                </div>
              </CardHeader>
            </Card>

            {/* Benefit 3 */}
            <Card 
              className={`bg-card/50 backdrop-blur-sm border-border transition-all duration-700 delay-200 hover:border-primary/50 hover:shadow-lg ${
                isVisible('benefit-3') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              id="benefit-3"
              ref={(el: HTMLDivElement | null) => (sectionRefs.current['benefit-3'] = el)}
            >
              <CardHeader>
                <div className="flex flex-col items-center text-center gap-3">
                  <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Brain className="h-6 w-6 text-primary" />
                  </div>
                  <CardTitle className="text-lg">Журнал изменений</CardTitle>
                  <CardDescription className="text-sm">
                    Связывайте события с цифрами прямо в таблице. Не гадайте, почему вчера всё было хорошо
                  </CardDescription>
                </div>
              </CardHeader>
            </Card>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="container mx-auto px-4 py-20 bg-muted/30">
        <div className="max-w-3xl mx-auto">
          <div 
            className={`text-center mb-12 transition-all duration-700 ${
              isVisible('how-it-works-title') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            id="how-it-works-title"
            ref={(el: HTMLDivElement | null) => (sectionRefs.current['how-it-works-title'] = el)}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4 flex items-center justify-center gap-2">
              <Wrench className="h-8 w-8 text-primary" />
              Как это работает?
            </h2>
            <p className="text-lg text-muted-foreground">Проще некуда</p>
          </div>

          <div className="space-y-0">
            {[
              { step: 1, title: 'Подключаете API-ключ от WB', icon: Zap },
              { step: 2, title: 'Указываете себестоимость товаров', icon: DollarSign },
              { step: 3, title: 'Видите готовую таблицу с вашей реальной прибылью', icon: BarChart3 },
              { step: 4, title: 'Комментируете изменения и управляете бизнесом осознанно', icon: Brain },
            ].map((item, index) => (
              <div key={item.step} className="relative">
                {/* Соединительная линия */}
                {index < 3 && (
                  <div className="absolute left-6 top-16 w-0.5 h-full bg-border z-0"></div>
                )}
                
                <div
                  className={`relative flex items-start gap-4 pb-8 transition-all duration-700 ${
                    isVisible(`step-${item.step}`) ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
                  }`}
                  style={{ transitionDelay: `${index * 150}ms` }}
                  id={`step-${item.step}`}
                  ref={(el: HTMLDivElement | null) => (sectionRefs.current[`step-${item.step}`] = el)}
                >
                  {/* Номер шага с иконкой */}
                  <div className="relative z-10 flex-shrink-0">
                    <div className="h-12 w-12 rounded-full bg-primary/10 border-2 border-primary/20 flex items-center justify-center">
                      <div className="absolute inset-0 rounded-full bg-primary/5 animate-pulse"></div>
                      <item.icon className="h-6 w-6 text-primary relative z-10" />
                    </div>
                    <div className="absolute -top-1 -left-1 h-6 w-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">
                      {item.step}
                    </div>
                  </div>

                  {/* Текст шага */}
                  <div className="flex-1 pt-2">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-semibold text-primary">Шаг {item.step}</span>
                    </div>
                    <p className="text-lg font-medium text-foreground">{item.title}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* What You Get Section */}
      <section className="container mx-auto px-4 py-20">
        <div className="max-w-5xl mx-auto">
          <div 
            className={`text-center mb-16 transition-all duration-700 ${
              isVisible('what-you-get-title') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            id="what-you-get-title"
            ref={(el: HTMLDivElement | null) => (sectionRefs.current['what-you-get-title'] = el)}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4 flex items-center justify-center gap-2">
              <Target className="h-8 w-8 text-primary" />
              Что вы получаете?
            </h2>
          </div>

          <div className="space-y-8">
            {[
              { 
                icon: TrendingUp, 
                text: 'Маржинальность с учётом ВСЕХ затрат',
                highlight: 'ВСЕХ'
              },
              { 
                icon: BarChart3, 
                text: 'Эффективность рекламы (ACOS, CPC) в разрезе кампаний',
                highlight: 'ACOS, CPC'
              },
              { 
                icon: Package, 
                text: 'Контроль остатков и оборачиваемости',
                highlight: 'Контроль'
              },
              { 
                icon: History, 
                text: 'Историю решений в «Журнале изменений»',
                highlight: 'Журнале изменений'
              },
              { 
                icon: Eye, 
                text: 'Спокойствие, потому что все ключевые цифры — перед глазами',
                highlight: 'ключевые цифры'
              },
              { 
                icon: Sparkles, 
                text: 'Самое главное — точки роста прибыли!!!',
                highlight: 'точки роста прибыли'
              },
            ].map((item, index) => {
              return (
                <div
                  key={index}
                  className={`flex items-start gap-6 transition-all duration-700 ${
                    isVisible(`what-you-get-${index}`) 
                      ? 'opacity-100 translate-x-0' 
                      : 'opacity-0 -translate-x-8'
                  }`}
                  style={{ transitionDelay: `${index * 100}ms` }}
                  id={`what-you-get-${index}`}
                  ref={(el: HTMLDivElement | null) => {
                    sectionRefs.current[`what-you-get-${index}`] = el
                  }}
                >
                  {/* Иконка с фоном */}
                  <div className="flex-shrink-0">
                    <div className="h-16 w-16 rounded-xl bg-gradient-to-br from-purple-500/20 to-purple-600/10 flex items-center justify-center border border-purple-500/20 shadow-lg">
                      <item.icon className="h-8 w-8 text-purple-600 dark:text-purple-400" />
                    </div>
                  </div>
                  
                  <div className="flex-1 pt-2">
                    <p className="text-lg md:text-xl text-foreground leading-relaxed">
                      {(() => {
                        const highlightIdx = item.text.indexOf(item.highlight)
                        if (highlightIdx < 0) return item.text
                        return (
                          <>
                            {item.text.slice(0, highlightIdx)}
                            <span className="font-bold">{item.highlight}</span>
                            {item.text.slice(highlightIdx + item.highlight.length)}
                          </>
                        )
                      })()}
                    </p>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="container mx-auto px-4 py-20">
        <div className="max-w-4xl mx-auto">
          <div 
            className={`relative transition-all duration-700 ${
              isVisible('final-cta') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            id="final-cta"
            ref={(el: HTMLDivElement | null) => (sectionRefs.current['final-cta'] = el)}
          >
            {/* Декоративный фон */}
            <div className="absolute -inset-4 bg-gradient-to-br from-purple-500/20 via-purple-600/10 to-purple-500/20 rounded-3xl blur-xl"></div>
            <div className="absolute -inset-2 bg-gradient-to-br from-purple-600/10 to-purple-500/5 rounded-3xl"></div>
            
            {/* Контент */}
            <div className="relative bg-card/80 backdrop-blur-sm border border-border/50 rounded-2xl p-8 md:p-12 shadow-2xl">
              <div className="text-center space-y-6">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-600 dark:text-purple-400 text-sm font-medium mb-2">
                  <TrendingUp className="h-4 w-4" />
                  <span>Начните прямо сейчас</span>
                </div>
                
                <h2 className="text-3xl md:text-4xl font-bold leading-tight">
                  Попробуйте увеличить свою реальную прибыль на Wildberries за 3 дня
                </h2>
                
                <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                  <span className="relative inline-block pb-1">
                    Никакой карты для пробного периода
                    <span 
                      className={`absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-red-500 via-red-600 to-red-500 rounded-full transition-all duration-1000 ease-out ${
                        isVisible('final-cta') ? 'w-full' : 'w-0'
                      }`}
                      style={{ 
                        transitionDelay: isVisible('final-cta') ? '300ms' : '0ms'
                      }}
                    ></span>
                  </span>
                  {' '}Только ваш API-ключ от WB.
                </p>
                
                <div className="pt-4 flex flex-col items-center gap-4">
                  <div className="flex flex-col sm:flex-row gap-4 items-center justify-center w-full">
                    <Button 
                      asChild 
                      size="lg" 
                      className="text-base px-8 group bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white shadow-lg shadow-purple-500/50 hover:shadow-xl hover:shadow-purple-500/60 transition-all duration-150 hover:scale-105"
                    >
                      <Link href="/auth/register" className="flex items-center gap-2">
                        Подключить РНП
                        <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                      </Link>
                    </Button>
                    <Button 
                      size="lg" 
                      variant="outline" 
                      className="text-base px-8 bg-transparent !transition-[background-color,border-color,box-shadow] !duration-200 ease-out hover:shadow-md hover:border-primary/40"
                      onClick={handleStartGuestDemo}
                      disabled={isStartingGuestDemo}
                    >
                      {isStartingGuestDemo ? 'Открываем демо...' : 'Посмотреть демо-таблицу'}
                    </Button>
                  </div>
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-white text-sm font-semibold" style={{ backgroundColor: '#fd9b33' }}>
                    <Gift className="h-4 w-4 stroke-white" strokeWidth={2} />
                    <span>3 дня бесплатно</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Screenshot Modal */}
      {isScreenshotOpen && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-md"
          style={{ 
            opacity: isScreenshotAnimating ? 1 : 0,
            transition: 'opacity 0.5s ease-out'
          }}
          onClick={() => {
            setIsScreenshotAnimating(false)
            setTimeout(() => setIsScreenshotOpen(false), 500)
          }}
        >
          <div 
            className="relative w-[90vw] max-w-5xl h-[85vh] flex items-center justify-center p-4"
            style={{ 
              transform: isScreenshotAnimating ? 'scale(1)' : 'scale(0.5)',
              opacity: isScreenshotAnimating ? 1 : 0,
              transition: 'transform 0.7s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.7s ease-out'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => {
                setIsScreenshotAnimating(false)
                setTimeout(() => setIsScreenshotOpen(false), 500)
              }}
              className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-10 bg-black/50 rounded-full p-2 hover:bg-black/70"
              aria-label="Закрыть"
            >
              <X className="h-6 w-6" />
            </button>
            <div className="relative w-full h-full flex items-center justify-center">
              <Image 
                src="/main.png" 
                alt="РНП интерфейс - все метрики на одном экране" 
                width={1200}
                height={800}
                className="max-w-full max-h-full w-auto h-auto object-contain rounded-lg shadow-2xl"
              />
            </div>
          </div>
        </div>
      )}

      {/* Pricing Section */}
      <section className="container mx-auto px-4 py-20">
        <div className="max-w-4xl mx-auto">
          <div 
            className={`text-center mb-12 transition-all duration-700 ${
              isVisible('pricing-title') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            id="pricing-title"
            ref={(el: HTMLDivElement | null) => (sectionRefs.current['pricing-title'] = el)}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Тарифы
            </h2>
            <p className="text-lg text-muted-foreground">
              Выберите подходящий период подписки
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* 1 месяц */}
            <Card 
              className={`bg-card/50 backdrop-blur-sm border-border hover:border-primary/50 transition-all flex flex-col ${
                isVisible('pricing-1') ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-12 scale-95'
              }`}
              style={{ transitionDelay: '0ms', transitionDuration: '800ms' }}
              id="pricing-1"
              ref={(el: HTMLDivElement | null) => (sectionRefs.current['pricing-1'] = el)}
            >
              <CardHeader className="text-center pb-4 pt-6 flex-1 flex flex-col justify-center">
                <CardTitle className="text-xl mb-2">1 месяц</CardTitle>
                <div className="text-3xl font-bold text-primary mb-1">4 990 ₽</div>
                <CardDescription className="text-base">Без скидки</CardDescription>
              </CardHeader>
            </Card>

            {/* 3 месяца */}
            <Card 
              className={`bg-card/50 backdrop-blur-sm border-border relative overflow-visible transition-all pricing-glow-sm pricing-pulse-sm flex flex-col ${
                isVisible('pricing-3') ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-12 scale-95'
              }`}
              style={{ transitionDelay: '400ms', transitionDuration: '800ms' }}
              id="pricing-3"
              ref={(el: HTMLDivElement | null) => (sectionRefs.current['pricing-3'] = el)}
            >
              <div className="absolute -top-3 -right-3 bg-gradient-to-br from-green-500 to-green-600 text-white text-xs font-bold px-4 py-1.5 rounded-full shadow-lg select-none pointer-events-none z-10 border-2 border-white/20">
                СКИДКА 10%
              </div>
              <CardHeader className="text-center pb-4 pt-6 flex-1 flex flex-col justify-center">
                <CardTitle className="text-xl mb-2">3 месяца</CardTitle>
                <div className="text-3xl font-bold text-primary mb-1">4 490 ₽</div>
                <CardDescription className="text-base mb-1">в месяц</CardDescription>
                <div className="flex items-center justify-center gap-2 text-sm text-green-600 dark:text-green-400 font-semibold">
                  <Check className="h-4 w-4" />
                  <span>-1 500 ₽ за период</span>
                </div>
              </CardHeader>
            </Card>

            {/* 6 месяцев */}
            <Card 
              className={`bg-card/50 backdrop-blur-sm border-border relative overflow-visible transition-all pricing-glow-md pricing-pulse-md flex flex-col ${
                isVisible('pricing-6') ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-12 scale-95'
              }`}
              style={{ transitionDelay: '800ms', transitionDuration: '800ms' }}
              id="pricing-6"
              ref={(el: HTMLDivElement | null) => (sectionRefs.current['pricing-6'] = el)}
            >
              <div className="absolute -top-3 -right-3 bg-gradient-to-br from-blue-500 to-blue-600 text-white text-xs font-bold px-4 py-1.5 rounded-full shadow-lg select-none pointer-events-none z-10 border-2 border-white/20">
                СКИДКА 20%
              </div>
              <CardHeader className="text-center pb-4 pt-6 flex-1 flex flex-col justify-center">
                <CardTitle className="text-xl mb-2">6 месяцев</CardTitle>
                <div className="text-3xl font-bold text-primary mb-1">3 990 ₽</div>
                <CardDescription className="text-base mb-1">в месяц</CardDescription>
                <div className="flex items-center justify-center gap-2 text-sm text-green-600 dark:text-green-400 font-semibold">
                  <Check className="h-4 w-4" />
                  <span>-6 000 ₽ за период</span>
                </div>
              </CardHeader>
            </Card>

            {/* 12 месяцев */}
            <Card 
              className={`bg-card/50 backdrop-blur-sm border-border relative overflow-visible transition-all pricing-glow-lg pricing-pulse-lg flex flex-col ${
                isVisible('pricing-12') ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-12 scale-95'
              }`}
              style={{ transitionDelay: '1200ms', transitionDuration: '800ms' }}
              id="pricing-12"
              ref={(el: HTMLDivElement | null) => (sectionRefs.current['pricing-12'] = el)}
            >
              <div className="absolute -top-3 -right-3 bg-gradient-to-br from-purple-500 to-purple-600 text-white text-xs font-bold px-4 py-1.5 rounded-full shadow-lg select-none pointer-events-none z-10 border-2 border-white/20">
                СКИДКА 30%
              </div>
              <CardHeader className="text-center pb-4 pt-6 flex-1 flex flex-col justify-center">
                <CardTitle className="text-xl mb-2">12 месяцев</CardTitle>
                <div className="text-3xl font-bold text-primary mb-1">3 490 ₽</div>
                <CardDescription className="text-base mb-1">в месяц</CardDescription>
                <div className="flex items-center justify-center gap-2 text-sm text-green-600 dark:text-green-400 font-semibold">
                  <Check className="h-4 w-4" />
                  <span>-18 000 ₽ за период</span>
                </div>
              </CardHeader>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border mt-20 relative">
        <div className="container mx-auto px-4 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2 rnp-logo">
              <img 
                src={LOGO_SRC} 
                alt="РНП" 
                className="h-4 w-4"
              />
              <span className="text-sm font-medium">rnpwb.ru</span>
            </div>

            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <span>ИП Кульбеда Василий Максимович</span>
              <span>•</span>
              <span>772421058356</span>
            </div>

            <p className="text-sm text-muted-foreground">© 2025 РНП. Рука на пульсе ваших продаж.</p>
          </div>
        </div>
      </footer>
    </div>
  )
}
